import { useMemo } from "react";
import { useSelector } from "react-redux";
import { partitionPaymentMethodsForRedemption } from "../program";
import {
  selectActiveLoyaltyProvider,
  selectLoyaltyDiscount,
  selectLoyaltyProgramDetails,
} from "../selectors";

/**
 * Which payment modes may settle a bill that has redeemed points on it.
 *
 * ```ts
 * const { allowed, excluded } = useRedemptionPaymentModes(paymentMethods);
 * // render `allowed` as usual; render `excluded` disabled under EXCLUDED_PAYMENT_MODES_NOTICE
 * ```
 *
 * The three store reads and the provider pairing are here rather than in a view because the POS
 * payment panel and the mobile one both need them, and only one of them had them: this was
 * spelled inline in `apps/web`'s `AllPaymentMethods` and **entirely absent from the mobile
 * one**, so a mobile cashier could settle a points-redeemed bill with a mode the programme bars.
 * The rule is `partitionPaymentMethodsForRedemption`; this only supplies it the store's answers.
 *
 * ⚠️ Safe to call unconditionally. With no points on the bill, no programme, or a provider that
 * publishes none, `allowed` is the list that came in — same reference — and `excluded` is empty,
 * so a caller needs no gate of its own.
 */
export function useRedemptionPaymentModes<T extends { mode?: string }>(
  paymentMethods: T[] | null | undefined,
): { allowed: T[]; excluded: T[] } {
  const loyaltyDiscount = useSelector(selectLoyaltyDiscount);
  const activeProvider = useSelector(selectActiveLoyaltyProvider);
  const programDetails = useSelector(selectLoyaltyProgramDetails);

  // Presence of the loyalty line is the whole trigger; its points/rate are irrelevant here, so
  // this does not re-partition every time the cashier changes the redeemed amount.
  const isPointsApplied = !!loyaltyDiscount;
  const providerMetaId = activeProvider?.metaId;

  return useMemo(
    () =>
      partitionPaymentMethodsForRedemption(paymentMethods, {
        isPointsApplied,
        providerMetaId,
        programDetails,
      }),
    [paymentMethods, isPointsApplied, providerMetaId, programDetails],
  );
}
